/**
 * Path: packages/frontend/src/core/composables/useSelectOptions.ts
 * Task: Provides computed properties and methods to generate select options for UI dropdowns, resolving dynamic entity types and static collections.
 */

import { computed } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useDatastoreStore } from '@/store/datastore';
import { useProjectStore } from '@/store/project';

export function useSelectOptions() {
  const authStore = useAuthStore();
  const datastore = useDatastoreStore();
  const projectStore = useProjectStore();

  // Maps entity types to their visual info (color, icon, shape)
  const entityInfoMap = computed(() => {
    const map: Record<string, any> = {};
    const entities = authStore.appData?.entities || [];

    entities.forEach((e: any) => {
      map[e.name] = {
        title: e.title || e.name,
        color: e.color || '#9E9E9E',
        icon: e.symbol || 'mdi-shape',
        iconShape: e.iconShape || 'squircle',
        isArchived: e.isArchived || false
      };
    });

    return map;
  });

  // Returns all active entity types as options (e.g., for creating a new entity or the FilterBuilder)
  const entityTypeOptions = computed(() => {
    const entities = authStore.appData?.entities || [];

    return entities.map((e: any) => ({
      key: e.name,
      title: e.title || e.name,
      color: e.color || '#9E9E9E',
      icon: e.symbol || 'mdi-shape',
      iconShape: e.iconShape || 'squircle',
      isArchived: e.isArchived || false
    })).sort((a: any, b: any) => a.title.localeCompare(b.title));
  });

  // Provides a subset of entity types that are actively part of a sequence/workflow relation
  const workflowEntityTypeOptions = computed(() => {
    const workflowTypes = new Set<string>();
    const relations = authStore.appData?.relations || [];

    relations.forEach((rel: any) => {
      if (rel.semanticType === 'sequence') {
        if (rel.sourceTypes) rel.sourceTypes.forEach((t: string) => workflowTypes.add(t));
        if (rel.targetTypes) rel.targetTypes.forEach((t: string) => workflowTypes.add(t));
      }
    });

    // Fallback: If no sequences are defined at all, return everything.
    if (workflowTypes.size === 0) return entityTypeOptions.value;

    return entityTypeOptions.value.filter(t => workflowTypes.has(t.key));
  });

  // Main function to fetch options for any given field
  const getSelectOptions = (entitySource: string, fieldId: string = '') => {
    if (!entitySource) return [];

    // --- PHASE 1: Static Collections (Periods, Weeks, Weekdays, Statuses...) ---
    if (entitySource === 'periods') {
      if (datastore.periods) {
        return Object.entries(datastore.periods).map(([key, val]) => ({
          id: key, title: val.label
        }));
      }
      return [];
    }

    if (entitySource === 'weeks') {
      const weeks = [];
      for(let i=1; i<=52; i++) weeks.push({ id: i, title: `Week ${i}` });
      return weeks;
    }

    if (entitySource === 'weekdays') {
      const days = authStore.appData?.weekdays || {};
      return Object.entries(days).map(([key, val]: [string, any]) => ({
        id: key, title: val.name
      }));
    }

    if (entitySource === 'statusCategories') {
      const categories = authStore.appData?.statusCategories || {};
      return Object.entries(categories).map(([key, val]: [string, any]) => ({
        id: key,
        title: val.label || val.name || key,
        color: val.color || '#9E9E9E',
        icon: val.icon || 'mdi-circle',
        iconShape: val.iconShape || 'circle',
        entityType: 'statusCategories'
      }));
    }

    if (entitySource === 'statusTypes' || entitySource === 'statuses') {
      const statuses = authStore.appData?.statusTypes || [];
      return statuses.map((s: any) => {
        let catColor = s.color || '#9E9E9E';
        let catIcon = s.icon || 'mdi-circle';
        let catShape = s.iconShape || 'circle';

        // Inherit styling from the parent category if defined
        if (s.system && authStore.appData?.statusCategories?.[s.system]) {
          const cat = authStore.appData.statusCategories[s.system];
          catColor = s.color || cat.color || catColor;
          catIcon = s.icon || cat.icon || catIcon;
          catShape = s.iconShape || cat.iconShape || catShape;
        }

        return {
          id: s.id || s.system_key,
          title: s.name_en || s.name_ger || s.id,
          color: catColor,
          icon: catIcon,
          iconShape: catShape,
          entityType: 'statusTypes'
        };
      });
    }

    // --- PHASE 2: Dynamic Relation Aggregation ---
    const data = datastore.data;
    if (!data) return [];

    // Get the list of entity types currently activated by the Project Administrator
    const activeProjectTypes = projectStore.loadedProject?.value
      ? (projectStore.projectsMetadata[projectStore.loadedProject.value]?.entityTypes || [])
      : [];

    const relationDef = authStore.appData?.relations?.find((r: any) => r.id === entitySource);

    if (relationDef) {
      // Legacy bridge: predecessorIds is the inverse property of the predecessors relation
      const isInverse = fieldId.startsWith('inverse_') || fieldId === 'predecessorIds';
      const allowedTypes = isInverse ? relationDef.sourceTypes : relationDef.targetTypes;

      if (!allowedTypes || !Array.isArray(allowedTypes)) return [];

      let combinedList: any[] = [];

      allowedTypes.forEach((type: string) => {
        // SECURITY CHECK: Skip entity types that are deactivated in this project
        if (activeProjectTypes.length > 0 && !activeProjectTypes.includes(type)) return;

        const list = data[type];
        if (Array.isArray(list)) {
          const entityDef = authStore.appData?.entities?.find((e: any) => e.name === type);
          const fallbackColor = entityDef?.color || entityInfoMap.value[type]?.color || '#9E9E9E';
          const fallbackIcon = entityDef?.symbol || entityInfoMap.value[type]?.icon || 'mdi-shape';

          const mapped = list.map((item: any) => ({
            ...item,
            title: item.title || item.name || item.id,
            entityType: type, // Vital for EntityIcon dynamic resolution
            color: item.color || fallbackColor,
            icon: item.icon || fallbackIcon
          }));

          combinedList = combinedList.concat(mapped);
        }
      });

      return combinedList.sort((a, b) => {
        const rankDiff = (a.rank || 0) - (b.rank || 0);
        if (rankDiff !== 0) return rankDiff;
        return (a.title || '').localeCompare(b.title || '');
      });
    }

    // --- PHASE 3: Fallback for direct Entity Type sources ---
    const isDirectType = authStore.appData?.entities?.some((e: any) => e.name === entitySource);

    if (isDirectType) {
      // SECURITY CHECK: Skip if this type is deactivated
      if (activeProjectTypes.length > 0 && !activeProjectTypes.includes(entitySource)) return [];

      const list = data[entitySource];
      if (Array.isArray(list)) {
        const entityDef = authStore.appData?.entities?.find((e: any) => e.name === entitySource);
        const fallbackColor = entityDef?.color || entityInfoMap.value[entitySource]?.color || '#9E9E9E';
        const fallbackIcon = entityDef?.symbol || entityInfoMap.value[entitySource]?.icon || 'mdi-shape';

        return list.map((item: any) => ({
          ...item,
          title: item.title || item.name || item.id,
          entityType: entitySource,
          color: item.color || fallbackColor,
          icon: item.icon || fallbackIcon
        })).sort((a, b) => {
          const rankDiff = (a.rank || 0) - (b.rank || 0);
          if (rankDiff !== 0) return rankDiff;
          return (a.title || '').localeCompare(b.title || '');
        });
      }
    }

    return [];
  };

  return {
    entityTypeOptions,
    workflowEntityTypeOptions,
    entityInfoMap,
    getSelectOptions
  };
}
